import { motion } from "framer-motion";

// Premium SVG dashboard mockup: Rxn3D "Case Overview"
export default function DashboardMockup({ className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox="0 0 720 500"
        role="img"
        aria-label="Rxn3D dashboard preview"
        className="w-full h-auto"
      >
        <defs>
          <linearGradient id="brand-g" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#2AA6DE" />
            <stop offset="55%" stopColor="#82298D" />
            <stop offset="100%" stopColor="#C9539F" />
          </linearGradient>
          <linearGradient id="row-g" x1="0" x2="1">
            <stop offset="0%" stopColor="#2AA6DE" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#82298D" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="chart-g" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#82298D" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#82298D" stopOpacity="0" />
          </linearGradient>
          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
            <feOffset dx="0" dy="6" result="offsetblur" />
            <feComponentTransfer><feFuncA type="linear" slope="0.18" /></feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* App shell */}
        <rect x="0" y="0" width="720" height="500" rx="20" fill="var(--tw-mockup-bg, #ffffff)" className="fill-white dark:fill-[#0B1120]" />
        <rect x="0" y="0" width="720" height="500" rx="20" fill="none" strokeWidth="1" className="stroke-black/10 dark:stroke-white/10" />

        {/* Top bar */}
        <rect x="16" y="16" width="688" height="36" rx="10" className="fill-black/[0.03] dark:fill-white/[0.04]" />
        <circle cx="34" cy="34" r="5" fill="url(#brand-g)" />
        <rect x="48" y="30" width="64" height="8" rx="4" className="fill-foreground/70" />
        <rect x="560" y="24" width="60" height="20" rx="10" className="fill-black/[0.06] dark:fill-white/[0.08]" />
        <rect x="630" y="24" width="60" height="20" rx="10" fill="url(#brand-g)" />

        {/* Sidebar */}
        <rect x="16" y="66" width="150" height="418" rx="14" className="fill-black/[0.02] dark:fill-white/[0.03]" />
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <g key={i}>
            <rect x="28" y={90 + i * 44} width="18" height="18" rx="5" className={i === 2 ? "" : "fill-foreground/25"} fill={i === 2 ? "url(#brand-g)" : undefined} />
            <rect x="54" y={95 + i * 44} width={i === 2 ? 82 : 70 - (i % 3) * 8} height="8" rx="4" className="fill-foreground/50" />
          </g>
        ))}
        {/* active pill */}
        <rect x="22" y="176" width="138" height="30" rx="10" className="fill-black/[0.06] dark:fill-white/[0.05]" />

        {/* Main area: Stats */}
        <g transform="translate(180,74)">
          {[
            { label: "Cases", value: "1,284", accent: false },
            { label: "In Production", value: "216", accent: true },
            { label: "Delivered (7d)", value: "374", accent: false },
          ].map((s, i) => (
            <g key={i} transform={`translate(${i * 170},0)`}>
              <rect width="160" height="90" rx="14" className="fill-black/[0.03] dark:fill-white/[0.04]" />
              <rect x="14" y="16" width="60" height="7" rx="3.5" className="fill-foreground/50" />
              <text x="14" y="58" className="fill-foreground font-heading" fontSize="26" fontWeight="700">{s.value}</text>
              <rect x="14" y="70" width={s.accent ? 70 : 40} height="6" rx="3" fill={s.accent ? "url(#brand-g)" : "currentColor"} className={s.accent ? "" : "fill-foreground/25"} />
            </g>
          ))}
        </g>

        {/* Chart card */}
        <g transform="translate(180,180)" filter="url(#softShadow)">
          <rect width="330" height="290" rx="16" className="fill-black/[0.02] dark:fill-white/[0.03]" />
          <rect x="16" y="16" width="90" height="8" rx="4" className="fill-foreground/60" />
          <rect x="16" y="30" width="60" height="6" rx="3" className="fill-foreground/30" />

          {/* Area chart */}
          <motion.path
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            d="M20,220 C60,180 90,200 120,160 C150,120 190,150 220,110 C260,60 290,100 310,80"
            stroke="url(#brand-g)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M20,220 C60,180 90,200 120,160 C150,120 190,150 220,110 C260,60 290,100 310,80 L310,260 L20,260 Z"
            fill="url(#chart-g)"
            opacity="0.6"
          />
          {/* gridlines */}
          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1="20"
              x2="310"
              y1={90 + i * 45}
              y2={90 + i * 45}
              className="stroke-foreground/10"
              strokeDasharray="3 4"
            />
          ))}
        </g>

        {/* Case table */}
        <g transform="translate(524,180)">
          <rect width="180" height="290" rx="16" className="fill-black/[0.02] dark:fill-white/[0.04]" />
          <rect x="14" y="16" width="80" height="8" rx="4" className="fill-foreground/60" />
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <g key={i} transform={`translate(14,${44 + i * 38})`}>
              <circle cx="8" cy="12" r="6" fill={i === 1 ? "url(#brand-g)" : undefined} className={i !== 1 ? "fill-foreground/25" : ""} />
              <rect x="22" y="4" width={90 - (i % 3) * 10} height="7" rx="3.5" className="fill-foreground/60" />
              <rect x="22" y="16" width={50 - (i % 4) * 5} height="6" rx="3" className="fill-foreground/25" />
              <motion.rect
                initial={{ width: 0 }}
                animate={{ width: 32 + (i % 4) * 20 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.08 }}
                x="120"
                y="10"
                height="6"
                rx="3"
                fill={i === 1 ? "url(#row-g)" : undefined}
                className={i !== 1 ? "fill-foreground/40" : ""}
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
